import { useNavigate } from 'react-router-dom';
import { Sparkles, Shield, Zap, MessageSquare, CreditCard, Bell } from 'lucide-react';
import TestimonialBanner from '@/components/TestimonialBanner';

function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "AI-Powered Chat Support",
      description: "Get instant help from our AI assistant, escalate to human support anytime"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Lightning Fast Response",
      description: "Real-time chat and ticket tracking for your tech support needs"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Expert Tech Solutions",
      description: "Remote support, PC cleanup, Wi-Fi optimization, and more"
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: "Flexible Payments",
      description: "Pay securely with Stripe, PayPal, or peer-to-peer options"
    },
    {
      icon: <Bell className="w-8 h-8" />,
      title: "Smart Notifications",
      description: "Stay updated via email and in-app notifications"
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Self-Service Portal",
      description: "Manage tickets, view history, and track progress"
    }
  ];

  const services = [
    { icon: "⚡", name: "Performance and PC Optimization", price: "$95", hasFee: true },
    { icon: "📡", name: "WiFi and Network Setup", price: "$130", hasFee: true },
    { icon: "💾", name: "Data Recovery", price: "$175", hasFee: true },
    { icon: "💻", name: "Remote Device Troubleshooting", price: "$60", hasFee: false },
    { icon: "🖥️", name: "New PC Setup and Data Transfer", price: "$150", hasFee: true },
    { icon: "🛡️", name: "Virus and Spyware Removal", price: "$120", hasFee: true }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="grid-background absolute inset-0 opacity-30"></div>
        
        <nav className="relative z-10 px-6 py-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="text-3xl glow">⚡</div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-[#00f0ff] via-[#ff00ff] to-[#b026ff] text-transparent bg-clip-text">
                Next Wave Tech Help
              </h1>
            </div>
            <div className="space-x-4">
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2 border border-[#00f0ff] text-[#00f0ff] rounded-lg hover:bg-[#00f0ff] hover:text-[#0a0a1f] transition-all"
              >
                Login
              </button>
              <button
                onClick={() => navigate('/register')}
                className="px-6 py-2 btn-primary text-white rounded-lg font-semibold"
              >
                Get Started
              </button>
            </div>
          </div>
        </nav>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
          <h2 className="text-6xl font-bold mb-6 glow" style={{ color: '#00f0ff' }}>
            Tech Support,
            <br />
            <span className="bg-gradient-to-r from-[#ff00ff] to-[#b026ff] text-transparent bg-clip-text">
              Reimagined
            </span>
          </h2>
          <p className="text-xl text-[#00f0ff]/80 mb-10 max-w-2xl mx-auto">
            Experience the future of tech support with AI-powered assistance,
            real-time chat, and expert human support when you need it.
          </p>
          <button
            onClick={() => navigate('/register')}
            className="px-12 py-4 btn-primary text-white text-lg rounded-lg font-bold glow-box hover-lift"
          >
            Start Your Journey →
          </button>
        </div>
      </div>

      {/* Testimonial banner near hero */}
      <TestimonialBanner />

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <h3 className="text-4xl font-bold text-center mb-16 glow" style={{ color: '#ff00ff' }}>
          Why Choose Next Wave?
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="p-6 rounded-xl glass glow-box hover-lift cursor-pointer"
              data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="text-[#00f0ff] mb-4 glow">{feature.icon}</div>
              <h4 className="text-xl font-bold text-[#00f0ff] mb-2">{feature.title}</h4>
              <p className="text-[#00f0ff]/70">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Services Section */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <h3 className="text-4xl font-bold text-center mb-6 glow" style={{ color: '#b026ff' }}>
          Our Services
        </h3>
        
        {/* Diagnostic Fee Notice */}
        <div className="max-w-3xl mx-auto mb-12 p-6 rounded-xl border-2 border-[#ff00ff] bg-[#ff00ff]/10 glow-box-magenta">
          <div className="text-center">
            <p className="text-2xl font-bold text-[#ff00ff] mb-3 glow">💳 $75 Diagnostic Call Fee</p>
            <p className="text-[#00f0ff] text-lg mb-2">
              All services include a <span className="font-bold text-[#ff00ff]">$75 diagnostic call fee</span> that applies toward your total service cost!
            </p>
            <p className="text-[#00f0ff]/80 text-sm">
              <span className="font-semibold">Example:</span> Virus Removal is $120. Pay $75 upfront for diagnostics, then only $45 when service completes. <span className="text-[#00ff00] font-bold">No additional charges!</span>
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <div
              key={service.name}
              className={`p-8 rounded-xl glass hover-lift cursor-pointer text-center ${
                service.hasFee ? 'glow-box-purple' : 'glow-box border-2 border-[#00ff00]'
              }`}
              data-testid={`service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="text-5xl mb-4">{service.icon}</div>
              <h4 className="text-xl font-bold text-[#ff00ff] mb-2">{service.name}</h4>
              <p className="text-3xl font-bold text-[#00f0ff] glow">{service.price}</p>
              {service.hasFee ? (
                <p className="text-xs text-[#00f0ff]/60 mt-2">$75 diagnostic fee applies</p>
              ) : (
                <p className="text-xs text-[#00ff00] font-bold mt-2 glow">✨ FLAT RATE - No diagnostic fee!</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-6 py-20 text-center">
        <div className="p-12 rounded-2xl glass glow-box-magenta">
          <h3 className="text-4xl font-bold mb-6 glow" style={{ color: '#ff00ff' }}>
            Ready to Experience the Future?
          </h3>
          <p className="text-xl text-[#00f0ff]/80 mb-8">
            Join thousands of satisfied customers who trust Next Wave Tech Help
            for their tech support needs.
          </p>
          <button
            onClick={() => navigate('/register')}
            className="px-12 py-4 btn-primary text-white text-lg rounded-lg font-bold glow-box"
            data-testid="cta-get-started-button"
          >
            Get Started Free →
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#00f0ff]/30 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-[#00f0ff]/60">
          <p>© 2025 Next Wave Tech Help. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;